/**
 * COMPOSE guarantee (C) — Operad structure.
 *
 * Neutralizes ambient effect (G1) by ensuring that composed operations
 * preserve safety properties. Unsafe compositions are type errors.
 *
 * Agda source: Kernel/COMPOSE/Neutralization.agda
 * Realization fidelity: STRUCTURAL (composition types enforced; operad laws documented)
 * Safety class: Parametric (acquired immunity)
 * Neutralization mode: INERT — violations exist but cannot compose into exploits
 * Tier: 1 (type-theoretic)
 * Legacy name: COMP
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { Brand, Guarantee } from "../foundation/types.js";
import { COMPOSE_META } from "../shared/theorem-meta.js";
export { COMPOSE_META };

// ── Structure: Operad ──────────────────────────────────────────

/** A typed operation with declared input and output types. */
export interface TypedOperation<In, Out> {
  readonly inputType: string;
  readonly outputType: string;
  readonly apply: (input: In) => Out;
}

/** A composition certificate — witness that two operations can be safely composed. */
export type CompositionCertificate<A, B, C> = Brand<
  { readonly first: TypedOperation<A, B>; readonly second: TypedOperation<B, C> },
  "CompositionCertificate"
>;

/** Compose two operations safely — type system prevents mismatched composition. */
export function safeCompose<A, B, C>(
  first: TypedOperation<A, B>,
  second: TypedOperation<B, C>,
): TypedOperation<A, C> & { readonly certificate: CompositionCertificate<A, B, C> } {
  return {
    inputType: first.inputType,
    outputType: second.outputType,
    apply: (input: A): C => second.apply(first.apply(input)),
    certificate: { first, second } as CompositionCertificate<A, B, C>,
  };
}

export interface ComposeViolation {
  readonly __brand: "ComposeViolation";
  readonly kind: "unsafe-composition";
}

export interface ComposeStructure {
  readonly operations: readonly TypedOperation<unknown, unknown>[];
  readonly validateComposition: (op1Type: string, op2Type: string) => boolean;
}

export function createComposeGuarantee(
  structure: ComposeStructure,
): Guarantee<ComposeStructure, ComposeViolation> {
  return {
    structure,
    neutralize: (_violation: ComposeViolation): never => {
      throw new Error(
        "COMPOSE violation is structurally inert: " +
          "type-checked composition prevents unsafe operation chains",
      );
    },
    meta: COMPOSE_META,
  };
}
